import { Button } from "@/components/ui";
import { HamburgerMenuIcon } from "@radix-ui/react-icons";
import { useState, useEffect } from "react";
import { SidebarContent } from "./SidebarContent";
import { useHistoryList } from "@/store/HistoryStore";
import { useSessionStore } from "@/store/SessionStore";

export interface SidebarProps {
  onLoadHistory?: (sessionId: number) => void;
  onClearMessages?: () => void;
  mode?: "mobile" | "desktop" | "both";
  onNavigate?: (path: string) => void;
}

export function Sidebar({
  onLoadHistory,
  onClearMessages,
  mode = "both",
  onNavigate,
}: SidebarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedChat, setSelectedChat] = useState<number | null>(null);
  const sessionId = useSessionStore((state) => state.sessionId);
  const { historyData, mutate, loadMore, isLoadingMore, isReachingEnd } =
    useHistoryList();

  useEffect(() => {
    if (sessionId) {
      setSelectedChat(parseInt(sessionId, 10));
    } else {
      setSelectedChat(null);
    }
  }, [sessionId]);

  useEffect(() => {
    const handleHistoryListUpdated = () => {
      mutate();
    };

    window.addEventListener(
      "historyListUpdated",
      handleHistoryListUpdated as EventListener
    );

    return () => {
      window.removeEventListener(
        "historyListUpdated",
        handleHistoryListUpdated as EventListener
      );
    };
  }, [mutate]);

  useEffect(() => {
    if (isOpen) {
      mutate();
    }
  }, [isOpen, mutate]);

  const openDrawer = () => setIsOpen(true);
  const closeDrawer = () => setIsOpen(false);

  const showMobile = mode === "mobile" || mode === "both";
  const showDesktop = mode === "desktop" || mode === "both";

  return (
    <>
      {/* 移动端触发按钮 */}
      {showMobile && (
        <div className="md:hidden">
          <Button variant="ghost" size="sm" onClick={openDrawer}>
            <HamburgerMenuIcon className="w-5 h-5" />
          </Button>
        </div>
      )}
      {/* 移动端样式 */}
      {showMobile && (
        <div className="md:hidden">
          <div
            className={`
            fixed inset-0 z-40 bg-black/50 transition-opacity
            ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"}
          `}
            onClick={closeDrawer}
          />
          <div
            className={`
            fixed inset-y-0 left-0 z-50 w-80 max-w-[80vw] bg-background shadow-xl transition-transform duration-300 ease-in-out
            ${isOpen ? "translate-x-0" : "-translate-x-full"}
          `}
          >
            <SidebarContent
              historyData={historyData}
              onLoadHistory={onLoadHistory}
              onClearMessages={onClearMessages}
              onClose={closeDrawer}
              loadMore={loadMore}
              isLoadingMore={isLoadingMore}
              isReachingEnd={isReachingEnd}
              selectedChat={selectedChat}
              setSelectedChat={setSelectedChat}
              onNavigate={onNavigate}
            />
          </div>
        </div>
      )}
      {/* pc端样式 */}
      {showDesktop && (
        <aside className="hidden md:flex w-80 flex-col h-full shrink-0 border-r">
          <SidebarContent
            historyData={historyData}
            onLoadHistory={onLoadHistory}
            onClearMessages={onClearMessages}
            onClose={undefined}
            loadMore={loadMore}
            isLoadingMore={isLoadingMore}
            isReachingEnd={isReachingEnd}
            selectedChat={selectedChat}
            setSelectedChat={setSelectedChat}
            onNavigate={onNavigate}
          />
        </aside>
      )}
    </>
  );
}
